import { useEffect } from "react";

/**
 * Cornerstone theme preview toggle (Phase 1 scaffold).
 *
 * Adds/removes the `theme-v2` class on <html> based on:
 *   • `?theme=v2` in the URL, OR
 *   • `localStorage.getItem('cornerstone-theme') === 'v2'`
 *
 * When active, the CSS variables from `.theme-v2` in index.css take over
 * and shadcn components inherit the new palette automatically. All existing
 * business logic remains untouched — this is purely a visual scope switch.
 *
 * Usage during preview:
 *   • Append `?theme=v2` to any URL to preview
 *   • Or run in DevTools console:
 *       localStorage.setItem('cornerstone-theme','v2'); location.reload();
 *   • To revert:
 *       localStorage.removeItem('cornerstone-theme'); location.reload();
 */
export default function ThemePreviewToggle() {
  useEffect(() => {
    const applyTheme = () => {
      const url = new URL(window.location.href);
      const fromQuery = url.searchParams.get("theme");
      const fromStorage = localStorage.getItem("cornerstone-theme");
      if (fromQuery === "v2") localStorage.setItem("cornerstone-theme", "v2");
      if (fromQuery === "v1") localStorage.removeItem("cornerstone-theme");

      const active = (fromQuery || fromStorage || "v2") === "v2";
      document.documentElement.classList.toggle("theme-v2", active);

      // Small floating badge — only shown when explicitly opted in via query param,
      // hidden once v2 is the default.
      let badge = document.getElementById("cornerstone-theme-badge");
      if (fromQuery === "v2") {
        if (!badge) {
          badge = document.createElement("div");
          badge.id = "cornerstone-theme-badge";
          badge.className = "theme-v2-badge";
          badge.textContent = "v2 preview";
          badge.setAttribute("data-testid", "theme-v2-badge");
          document.body.appendChild(badge);
        }
      } else if (badge) {
        badge.remove();
      }
    };
    applyTheme();
    window.addEventListener("popstate", applyTheme);
    return () => window.removeEventListener("popstate", applyTheme);
  }, []);
  return null;
}
